/**
 * User-friendly error messages for API errors
 * Maps error categories to readable messages for display in the UI
 */
import type { ErrorCategory } from '@/services/api';

/**
 * Get a user-friendly error message based on error category
 * @param category - The error category from ApiClientError
 * @param requestId - Optional request ID to include for server errors (for support reference)
 * @returns A user-friendly error message
 */
export function getUserFriendlyMessage(
  category: ErrorCategory,
  requestId?: string
): string {
  switch (category) {
    case 'auth':
      return 'Your session has expired. Please sign in again.';
    case 'rate_limit':
      return 'Too many requests. Please wait a moment and try again.';
    case 'validation':
      return 'Please check your input and try again.';
    case 'server':
      // Include truncated request ID for support reference
      return `Something went wrong on our end. Please try again later.${
        requestId && requestId !== 'unknown'
          ? ` (Ref: ${requestId.slice(0, 8)})`
          : ''
      }`;
    case 'network':
      return 'Unable to connect. Please check your internet connection.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Get a user-friendly error message with retry time for rate limiting
 * @param retryAfter - Seconds until retry is allowed
 * @returns A user-friendly message with retry time
 */
export function getRateLimitMessage(retryAfter?: number): string {
  if (retryAfter && retryAfter > 0) {
    if (retryAfter < 60) {
      return `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
    } else {
      const minutes = Math.ceil(retryAfter / 60);
      return `Too many requests. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
    }
  }
  return 'Too many requests. Please wait a few minutes before trying again.';
}

/**
 * The message to show for a 429, or null if the error is not a 429.
 *
 * Core's household limits (e.g. flow generation, WP-CORE-8) answer 429 with a
 * `detail` sentence that already says when to retry ("... Please try again in
 * 37 minutes."). That sentence is preferred: browsers cannot read `Retry-After`
 * cross-origin (CORS does not expose it), so `retryAfter` is usually absent and
 * the generic fallback would only say "a few minutes".
 */
export function getRateLimitRetryMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const { status, detail, retryAfter } = error as {
    status?: number;
    detail?: unknown;
    retryAfter?: number;
  };
  if (status !== 429) return null;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return getRateLimitMessage(retryAfter);
}
